import { computed, ref } from 'vue'
import { useBooksySegment, isBooksyCom } from '@booksy-com/vue-tools'
import { useAnalyticsCommonParams } from './common-params'

import { SEGMENT_KEYS } from './consts'

type BooksySegmentInstance = ReturnType<typeof useBooksySegment>

type SegmentPageProperties = Record<string, unknown> | null

type SegmentInternals = BooksySegmentInstance & {
  _withAnalyticsGuard: (
    method: 'page',
    name: string,
    properties: SegmentPageProperties,
    options: unknown
  ) => void
  _getBaseOptions: () => unknown
}

let INSTANCE: BooksySegmentInstance | null = null

const SEGMENT_KEY = isBooksyCom()
  ? SEGMENT_KEYS.PRODUCTION
  : SEGMENT_KEYS.TEST

export function createSegment (): BooksySegmentInstance {
  if (!INSTANCE) {
    const {
      country,
      businessId,
      userId,
    } = useAnalyticsCommonParams()

    const userTraits = computed(() => ({
      userId: userId.value ?? '',
      ...(businessId.value && { business_id: businessId.value }),
      ...(country.value && { country: country.value }),
    }))

    INSTANCE = useBooksySegment({
      writeKey: SEGMENT_KEY,
      userTraits,
      ...(isBooksyCom()
        ? {}
        : {
          consents: ref({
            marketing: true,
          }),
        }),
    // @booksy-com/vue-tools SegmentConfig.Consents is stricter than the legacy marketing-only ref we pass in non-production.
    } as unknown as Parameters<typeof useBooksySegment>[0])

    const segmentInstance = INSTANCE as SegmentInternals

    // vue-tools useBooksySegment.page() does not forward properties to Segment.
    // Patch until upstream fix ships (tech debt: couples to _withAnalyticsGuard internals).
    segmentInstance.page = ((name: string, properties: SegmentPageProperties = null) => {
      segmentInstance._withAnalyticsGuard(
        'page',
        name,
        properties,
        segmentInstance._getBaseOptions()
      )
    }) as typeof segmentInstance.page
  }

  return INSTANCE
}
